<template>
  <div class="expert-editor space-y-6 pb-20 animate-[fadeIn_0.6s_ease-out]">
    <!-- 顶部返回栏 -->
    <div class="flex items-center gap-4">
      <button
        @click="goBack"
        class="flex items-center gap-1 text-sm text-slate-500 hover:text-[#F2C94C] transition-colors"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        返回专家列表
      </button>
      <h3 class="text-base font-black text-slate-900">{{ isEdit ? '编辑专家' : '新增专家' }}</h3>
    </div>

    <!-- 区块 1：基础信息 -->
    <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-6 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
      <div class="flex items-center gap-2 mb-4">
        <svg class="w-4 h-4 text-[#F2C94C]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
        <h4 class="text-sm font-black text-slate-900">基础信息</h4>
      </div>
      <div class="w-12 h-0.5 bg-[#F2C94C] mb-5"></div>

      <el-form :model="form" label-width="100px" size="small">
        <el-form-item label="姓名" required>
          <el-input v-model="form.name" placeholder="请输入专家姓名" />
        </el-form-item>
        <el-form-item label="头衔">
          <el-input v-model="form.title" placeholder="例如：前华为组织发展专家" />
        </el-form-item>
        <el-form-item label="所属公司">
          <el-input v-model="form.department" placeholder="例如：华为" />
        </el-form-item>
        <el-form-item label="职位">
          <el-input v-model="form.position" placeholder="例如：组织发展专家" />
        </el-form-item>
        <el-form-item label="所在城市">
          <el-input v-model="form.location" placeholder="例如：北京、上海、深圳..." />
        </el-form-item>

        <!-- 头像 -->
        <el-form-item label="头像">
          <div class="flex items-start gap-4">
            <div class="flex-1 space-y-2">
              <el-input v-model="form.avatar" placeholder="粘贴头像 URL..." />
              <div class="flex items-center gap-2">
                <input ref="avatarInput" type="file" accept="image/*" class="hidden" @change="handleAvatarSelect" />
                <button
                  @click="$refs.avatarInput.click()"
                  class="px-3 py-1.5 border border-slate-200 text-slate-600 text-xs rounded-lg hover:bg-slate-50 transition-colors"
                >
                  选择本地图片
                </button>
                <span class="text-xs text-amber-600">生产环境需接入云存储</span>
              </div>
            </div>
            <div class="w-16 h-16 rounded-full overflow-hidden border border-slate-200 bg-slate-50 flex-shrink-0">
              <img v-if="avatarPreview" :src="avatarPreview" class="w-full h-full object-cover" />
              <div v-else class="w-full h-full flex items-center justify-center text-slate-300 text-xs">无头像</div>
            </div>
          </div>
        </el-form-item>

        <!-- 个人简介 Markdown -->
        <el-form-item label="个人简介">
          <div class="mb-1 text-xs text-slate-400">支持 Markdown 格式</div>
          <div class="flex gap-4" style="height: 160px;">
            <el-input
              v-model="form.bio"
              type="textarea"
              placeholder="输入 Markdown 内容..."
              :style="{ height: '100%' }"
              :input-style="{ height: '100%', resize: 'none' }"
            />
            <div class="flex-1 border border-slate-200 rounded-lg p-3 overflow-y-auto bg-slate-50 text-sm" v-html="renderMarkdown(form.bio)"></div>
          </div>
        </el-form-item>

        <el-form-item label="名言">
          <el-input v-model="form.quote" placeholder="一句能代表专家核心理念的话..." />
        </el-form-item>
      </el-form>
    </div>

    <!-- 区块 2：专业标签 -->
    <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-6 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
      <div class="flex items-center gap-2 mb-4">
        <svg class="w-4 h-4 text-[#F2C94C]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
        </svg>
        <h4 class="text-sm font-black text-slate-900">专业标签</h4>
      </div>
      <div class="w-12 h-0.5 bg-[#F2C94C] mb-5"></div>

      <el-form :model="form" label-width="100px" size="small">
        <el-form-item label="擅长领域">
          <el-select v-model="form.topics" multiple placeholder="请选择擅长领域" style="width: 100%">
            <el-option v-for="t in allTopics" :key="t" :label="t" :value="t" />
          </el-select>
        </el-form-item>
        <el-form-item label="标签">
          <el-select
            v-model="form.tags"
            multiple
            filterable
            allow-create
            placeholder="请选择或输入标签"
            style="width: 100%"
          >
            <el-option v-for="t in allTags" :key="t" :label="t" :value="t" />
          </el-select>
        </el-form-item>
        <el-form-item label="履历">
          <el-select
            v-model="form.resume"
            multiple
            filterable
            allow-create
            placeholder="请输入履历条目"
            style="width: 100%"
          >
          </el-select>
        </el-form-item>
      </el-form>
    </div>

    <!-- 区块 3：内容资源 -->
    <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-6 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
      <div class="flex items-center gap-2 mb-4">
        <svg class="w-4 h-4 text-[#F2C94C]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <h4 class="text-sm font-black text-slate-900">内容资源</h4>
      </div>
      <div class="w-12 h-0.5 bg-[#F2C94C] mb-5"></div>

      <el-form :model="form" label-width="100px" size="small">
        <el-form-item label="案例文档">
          <div class="mb-1 text-xs text-slate-400">支持 Markdown 格式</div>
          <el-input
            v-model="form.caseDocument"
            type="textarea"
            :rows="6"
            placeholder="输入案例文档内容..."
          />
        </el-form-item>

        <!-- 音频 -->
        <el-form-item label="音频标题">
          <el-input v-model="form.audioTitle" placeholder="音频资源标题..." />
        </el-form-item>
        <el-form-item label="音频资源">
          <div class="space-y-2">
            <el-input v-model="form.audioUrl" placeholder="粘贴音频 URL..." />
            <div class="flex items-center gap-2">
              <input ref="audioInput" type="file" accept="audio/*" class="hidden" @change="handleAudioSelect" />
              <button
                @click="$refs.audioInput.click()"
                class="px-3 py-1.5 border border-slate-200 text-slate-600 text-xs rounded-lg hover:bg-slate-50 transition-colors"
              >
                选择本地文件
              </button>
              <span class="text-xs text-amber-600">生产环境需接入云存储</span>
            </div>
            <div v-if="audioPreviewUrl || form.audioUrl" class="border border-slate-200 rounded-lg p-3 bg-slate-50 max-w-md">
              <audio v-if="audioPreviewUrl || isValidUrl(form.audioUrl)" :src="audioPreviewUrl || form.audioUrl" controls class="w-full"></audio>
              <div v-else class="text-center text-slate-400 text-sm">无效的 URL</div>
            </div>
            <p v-if="audioFileName" class="text-xs text-slate-500">已选择：{{ audioFileName }}</p>
          </div>
        </el-form-item>

        <!-- 视频 -->
        <el-form-item label="视频标题">
          <el-input v-model="form.videoTitle" placeholder="视频资源标题..." />
        </el-form-item>
        <el-form-item label="视频资源">
          <div class="space-y-2">
            <el-input v-model="form.videoUrl" placeholder="粘贴视频 URL..." />
            <div class="flex items-center gap-2">
              <input ref="videoInput" type="file" accept="video/*" class="hidden" @change="handleVideoSelect" />
              <button
                @click="$refs.videoInput.click()"
                class="px-3 py-1.5 border border-slate-200 text-slate-600 text-xs rounded-lg hover:bg-slate-50 transition-colors"
              >
                选择本地文件
              </button>
              <span class="text-xs text-amber-600">生产环境需接入云存储</span>
            </div>
            <div v-if="videoPreviewUrl || form.videoUrl" class="border border-slate-200 rounded-lg overflow-hidden bg-slate-900 max-w-md">
              <video v-if="videoPreviewUrl || isValidUrl(form.videoUrl)" :src="videoPreviewUrl || form.videoUrl" controls class="w-full" style="max-height: 200px;"></video>
              <div v-else class="p-4 text-center text-slate-400 text-sm">无效的 URL</div>
            </div>
            <p v-if="videoFileName" class="text-xs text-slate-500">已选择：{{ videoFileName }}</p>
          </div>
        </el-form-item>
      </el-form>
    </div>

    <!-- 区块 4：运营配置 -->
    <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-6 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
      <div class="flex items-center gap-2 mb-4">
        <svg class="w-4 h-4 text-[#F2C94C]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        </svg>
        <h4 class="text-sm font-black text-slate-900">运营配置</h4>
      </div>
      <div class="w-12 h-0.5 bg-[#F2C94C] mb-5"></div>

      <el-form :model="form" label-width="120px" size="small">
        <el-form-item label="首页推荐">
          <el-switch v-model="form.isFeatured" />
        </el-form-item>
        <el-form-item label="推荐权重">
          <el-slider v-model="form.recommendWeight" :max="100" show-input style="width: 300px" />
        </el-form-item>
        <el-form-item label="状态">
          <el-radio-group v-model="form.status">
            <el-radio-button label="active">正常</el-radio-button>
            <el-radio-button label="suspended">停用</el-radio-button>
            <el-radio-button label="banned">禁用</el-radio-button>
          </el-radio-group>
        </el-form-item>
      </el-form>
    </div>

    <!-- 区块 5：统计数据（只读） -->
    <div class="bg-slate-50 border border-slate-200 rounded-lg p-6">
      <div class="flex items-center gap-2 mb-3">
        <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <h4 class="text-sm font-black text-slate-700">统计数据</h4>
      </div>
      <div class="text-xs text-slate-400 mb-4">以下数据由用户行为自动统计，不可手动修改</div>

      <el-form :model="form" label-width="100px" size="small">
        <el-form-item label="积分">
          <el-input v-model="form.points" disabled />
        </el-form-item>
        <el-form-item label="内容数量">
          <el-input v-model="form.contentCount" disabled />
        </el-form-item>
        <el-form-item label="点击数">
          <el-input v-model="form.stats.clicks" disabled />
        </el-form-item>
        <el-form-item label="播放数">
          <el-input v-model="form.stats.plays" disabled />
        </el-form-item>
        <el-form-item label="收藏数">
          <el-input v-model="form.stats.bookmarks" disabled />
        </el-form-item>
        <el-form-item label="点赞数">
          <el-input v-model="form.stats.likes" disabled />
        </el-form-item>
        <el-form-item label="评论数">
          <el-input v-model="form.stats.comments" disabled />
        </el-form-item>
      </el-form>
    </div>

    <!-- 底部操作栏 -->
    <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm px-6 py-4 flex items-center justify-end gap-3 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
      <button
        @click="goBack"
        class="px-5 py-2 border border-slate-200 text-slate-600 text-sm rounded-xl hover:bg-slate-50 transition-colors"
      >
        取消并返回
      </button>
      <button
        @click="handleSave"
        class="px-5 py-2 bg-[#F2C94C] text-white text-sm font-medium rounded-full hover:bg-[#E5B73B] transition-colors shadow-sm"
      >
        保存
      </button>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { logAction } from '../../services/audit'
import { calculateExpertPoints, calculateExpertContentCount, DEFAULT_EXPERT_STATS } from '../../data/experts'
import { EXPERTS_MASTER } from '../../data/experts'
import { SCORING_CONFIG, PERMISSIONS } from '../../data/adminConfig'
import { marked } from 'marked'

const DEFAULT_FORM = {
  id: '',
  name: '',
  avatar: '',
  title: '',
  department: '',
  position: '',
  location: '',
  bio: '',
  quote: '',
  topics: [],
  resume: [],
  tags: [],
  caseDocument: '',
  audioTitle: '',
  videoTitle: '',
  audioUrl: '',
  videoUrl: '',
  recommendWeight: 0,
  isFeatured: false,
  status: 'active',
  points: 0,
  contentCount: 0,
  stats: { ...DEFAULT_EXPERT_STATS },
  createdBy: null,
}

export default {
  name: 'ExpertEditor',

  data() {
    return {
      form: { ...DEFAULT_FORM },
      avatarPreview: '',
      avatarFileName: '',
      audioPreviewUrl: '',
      audioFileName: '',
      videoPreviewUrl: '',
      videoFileName: '',
      PERMISSIONS,
    }
  },

  computed: {
    ...mapState(['experts', 'cases']),
    ...mapGetters(['hasPermission', 'currentAdminId']),

    isEdit() {
      return !!this.$route.query.id
    },

    expertId() {
      return this.$route.query.id
    },

    allTopics() {
      const set = new Set()
      const list = this.experts || EXPERTS_MASTER
      list.forEach(e => e.topics?.forEach(t => set.add(t)))
      return Array.from(set)
    },

    allTags() {
      const set = new Set()
      const list = this.experts || EXPERTS_MASTER
      list.forEach(e => e.tags?.forEach(t => set.add(t)))
      return Array.from(set)
    },
  },

  created() {
    if (this.isEdit) {
      this.loadExpert()
    } else {
      this.form = { ...DEFAULT_FORM, id: `e${Date.now()}`, createdBy: this.currentAdminId }
    }
  },

  methods: {
    hasPerm(permission) {
      return this.hasPermission(permission)
    },

    loadExpert() {
      const list = this.experts || []
      const expert = list.find(e => e.id === this.expertId)
      if (!expert) {
        this.$message.error('专家不存在')
        this.goBack()
        return
      }
      this.form = {
        ...DEFAULT_FORM,
        ...JSON.parse(JSON.stringify(expert)),
      }
      if (this.form.avatar) {
        this.avatarPreview = this.form.avatar
      }
    },

    renderMarkdown(text) {
      if (!text) return '<p class="text-slate-400 text-sm">预览区域</p>'
      try {
        return marked(text)
      } catch {
        return '<p class="text-red-400 text-sm">Markdown 渲染错误</p>'
      }
    },

    isValidUrl(url) {
      if (!url) return false
      try { new URL(url); return true } catch { return false }
    },

    handleAvatarSelect(event) {
      const file = event.target.files[0]
      if (!file) return
      this.avatarFileName = file.name
      const reader = new FileReader()
      reader.onload = (e) => {
        this.avatarPreview = e.target.result
        this.form.avatar = e.target.result
      }
      reader.readAsDataURL(file)
      event.target.value = ''
    },

    handleAudioSelect(event) {
      const file = event.target.files[0]
      if (!file) return
      this.audioFileName = file.name
      const reader = new FileReader()
      reader.onload = (e) => {
        this.audioPreviewUrl = e.target.result
        this.form.audioUrl = e.target.result
      }
      reader.readAsDataURL(file)
      event.target.value = ''
    },

    handleVideoSelect(event) {
      const file = event.target.files[0]
      if (!file) return
      this.videoFileName = file.name
      const reader = new FileReader()
      reader.onload = (e) => {
        this.videoPreviewUrl = e.target.result
        this.form.videoUrl = e.target.result
      }
      reader.readAsDataURL(file)
      event.target.value = ''
    },

    goBack() {
      this.$router.push('/admin/experts')
    },

    handleSave() {
      if (!this.form.name.trim()) {
        this.$message.error('姓名不能为空')
        return
      }

      const list = [...(this.experts || EXPERTS_MASTER.map(e => ({ ...e, stats: { ...DEFAULT_EXPERT_STATS }, points: 0, contentCount: 0 })))]
      const savedForm = { ...this.form, name: this.form.name.trim() }

      // 重新计算派生数据
      savedForm.points = calculateExpertPoints(savedForm.stats, SCORING_CONFIG.weights)
      savedForm.contentCount = calculateExpertContentCount(savedForm.id, this.cases)

      if (!this.isEdit) {
        savedForm.createdAt = Date.now()
        savedForm.createdBy = this.currentAdminId
        list.push(savedForm)
      } else {
        const idx = list.findIndex(e => e.id === savedForm.id)
        if (idx !== -1) {
          list[idx] = savedForm
        }
      }

      this.$store.commit('SET_EXPERTS', list)

      logAction({
        action: this.isEdit ? 'UPDATE' : 'CREATE',
        targetType: 'expert',
        targetId: savedForm.id,
        reason: this.isEdit ? `编辑专家: ${savedForm.name}` : `新建专家: ${savedForm.name}`,
        source: 'admin-ui',
        actor: { role: this.$store.state.userRole },
      })

      this.$message.success(this.isEdit ? '保存成功' : '创建成功')
      this.goBack()
    },
  },
}
</script>

<style scoped>
.expert-editor {
  padding-bottom: 80px;
}
</style>
